import React, { forwardRef, InputHTMLAttributes } from 'react';
import clsx from 'clsx';
import styles from './Input.module.scss';
import Icon from '../Icon';
import Text from '../Text/Text';

export enum InputVariant {
  DEFAULT = 'default',
  ERROR = 'error',
  WARNING = 'warning',
  SUCCESS = 'success',
}

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'className' | 'onChange'> {
  label?: string;
  variant?: InputVariant;
  disabled?: boolean;
  className?: string;
  containerClassName?: string;
  showIcon?: boolean;
  textArea?: false;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  isWhite?: boolean;
  errorText?: string;
  text?: string;
}

export interface TextAreaProps extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'className' | 'onChange'> {
  label?: string;
  variant?: InputVariant;
  disabled?: boolean;
  className?: string;
  containerClassName?: string;
  showIcon?: boolean;
  textArea: true;
  onChange: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  isWhite?: boolean;
  errorText?: string;
  text?: string;
}

const Input = forwardRef<HTMLInputElement | HTMLTextAreaElement, InputProps | TextAreaProps>(
  (
    {
      label,
      variant = InputVariant.DEFAULT,
      disabled = false, className = '', containerClassName = '',
      showIcon = false,
      textArea = false, onChange,
      isWhite = false,
      errorText,
      text,
      ...props
    },
    ref,
  ) => {
    // Auto-detect variant based on error text
    const effectiveVariant = errorText ? InputVariant.ERROR : variant;

    const getIconClassName = () => {
      switch (effectiveVariant) {
        case InputVariant.ERROR:
          return styles.errorIcon;
        case InputVariant.WARNING:
          return styles.warningIcon;
        case InputVariant.SUCCESS:
          return styles.successIcon;
        default:
          return '';
      }
    };

    const getVariantClass = () => {
      switch (effectiveVariant) {
        case InputVariant.ERROR:
          return styles.error;
        case InputVariant.WARNING:
          return styles.warning;
        case InputVariant.SUCCESS:
          return styles.success;
        default:
          return '';
      }
    };

    const variantClass = getVariantClass();
    const inputClasses = clsx(
      styles.input,
      variantClass,
      disabled && styles.disabled,
      isWhite && styles.white,
      className,
    );

    const containerClasses = clsx(
      styles.inputContainer,
      showIcon && styles.withIcon,
    );

    const getIconName = () => {
      switch (effectiveVariant) {
        case InputVariant.ERROR:
          return 'error';
        case InputVariant.WARNING:
          return 'warning';
        case InputVariant.SUCCESS:
          return 'check_circle';
        default:
          return '';
      }
    };

    return (
      <div className={clsx(styles.inputWrapper)}>
        <div className={clsx(containerClasses, containerClassName)}>
          {!!label && (
            <label htmlFor={props.id}>
              <span>{label}</span>
            </label>
          )}
          {textArea ? (
            <textarea
              ref={ref as React.Ref<HTMLTextAreaElement>}
              className={inputClasses}
              disabled={disabled}
              data-textarea='true'
              onChange={onChange as React.ChangeEventHandler<HTMLTextAreaElement>}
              {...(props as Omit<TextAreaProps, 'onChange'>)}
            />
          ) : (
            <input
              ref={ref as React.Ref<HTMLInputElement>}
              className={inputClasses}
              disabled={disabled}
              onChange={onChange as React.ChangeEventHandler<HTMLInputElement>}
              {...(props as Omit<InputProps, 'onChange'>)}
            />
          )}
          {showIcon && getIconName() && (
            <Icon
              name={getIconName()}
              className={clsx(styles.inputIcon, getIconClassName())}
              style={{ fontSize: '22px' }}
            />
          )}
        </div>
        {text && (
          <Text
            tag='p'
            className={clsx(styles.text, {
              [styles.errorText]: effectiveVariant === InputVariant.ERROR,
              [styles.successText]: effectiveVariant === InputVariant.SUCCESS,
            })}
          >
            {text}
          </Text>
        )}
      </div>
    );
  },
);

export default Input;
